import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { forkJoin, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { ConfigService } from '../config.service';
import { MinistryType, TripMinistry } from './commonInterfaces';
import { DataTransform, DateField } from './dataTransformFunctions';
import { AssignedPartnershipInfo, PartnershipDropdownInfo, PartnershipInfo, PartnershipStatus, PartnerTypes, ProfilesCommunity, ProfilesContact, ProfilesPartner, TripsCommunity, TripsContact, TripsPartner } from './types/partnerships';

export interface MinistriesUpdatedArgs {
  tripId: number
}

export interface PartnershipsUpdatedArgs {
  subtripId: string
}

@Injectable({
  providedIn: 'root'
})
export class TripPartnershipService {

  readonly ministryTypes: MinistryType[] = ["children", "service", "church", "sports", "prayer", "school", "mercy", "evangelism", "youth", "adult", "medical", "other"]

  readonly ministryFormFields = this.ministryTypes.map(t => [t + '-checked', t + '-description']).reduce((prev, curr) => [...prev, ...curr])
  readonly partnerDateFields: DateField<ProfilesPartner>[] = [{ dateType: "utc", fieldName: "modified" }]
  readonly contactDateFields: DateField<ProfilesContact>[] = [{ dateType: "local", fieldName: "birthday" }, { dateType: "utc", fieldName: "modified" }]
  readonly communityDateFields: DateField<ProfilesCommunity>[] = [{ dateType: "utc", fieldName: "modified" }]
  readonly tripsPartnerDateFields: DateField<TripsPartner>[]  = [{dateType: "utc", fieldName: "timestamp"}]
  readonly tripsCommunityDateFields: DateField<TripsCommunity>[] = [{dateType: "utc", fieldName: "timestamp"}]
  readonly tripsContactDateFields: DateField<TripsContact>[] = [{dateType: "utc", fieldName: "timestamp"}]
 
  constructor(
    private http: HttpClient,
    private config: ConfigService,
  ) { }

  ministriesUpdated: Subject<MinistriesUpdatedArgs> = new Subject<MinistriesUpdatedArgs>()

  partnershipsUpdated: Subject<PartnershipsUpdatedArgs> = new Subject<PartnershipsUpdatedArgs>()

  getMinistries(tripId: number) {
    return this.http.get<TripMinistry[]>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/partnership/ministry`)
  }

  updateMinistries(tripId: number, ministries: TripMinistry[]) {
    return this.http.post(`${this.config.dataApiUrl}/trips/${tripId.toString()}/partnership/ministry`, { ministries: ministries }).pipe(
      tap(
        () => this.ministriesUpdated.next({ tripId: tripId })
      )
    )
  }

  getPartnerships(subtripId: string): Observable<AssignedPartnershipInfo> {
    return this.http.get<AssignedPartnershipInfo>(`${this.config.dataApiUrl}/subtrips/${subtripId}/partnership`).pipe(
      switchMap(partnerInfo => {
       return this.replacePartnerAssignmentDates(partnerInfo)
      })
    )
  }

  getPartnershipDropdownOptions(tripId: number): Observable<PartnershipDropdownInfo> {
    return this.http.get<PartnershipDropdownInfo>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/partnership/available-partnerships`, {params: {dropdown: true}})
  }

  addPartnership(subtripId: string, partnerId: string): Observable<AssignedPartnershipInfo> {
    return this.http.post<AssignedPartnershipInfo>(`${this.config.dataApiUrl}/subtrips/${subtripId}/partnership/partner`, {partnerId: partnerId}).pipe(
      switchMap(partnerInfo => {
        return this.replacePartnerAssignmentDates(partnerInfo)
      }),
      tap(result => {
        this.partnershipsUpdated.next({subtripId: subtripId})
      })
    )
  }

  addCommunity(subtripId: string, communityId: string): Observable<AssignedPartnershipInfo> {
    return this.http.post<AssignedPartnershipInfo>(`${this.config.dataApiUrl}/subtrips/${subtripId}/partnership/community`, {communityId: communityId}).pipe(
      switchMap(partnerInfo => {
        return this.replacePartnerAssignmentDates(partnerInfo)
      }),
      tap(result => {
        this.partnershipsUpdated.next({subtripId: subtripId})
      })
    )
  }

  addContact(subtripId: string, contactId: string): Observable<AssignedPartnershipInfo> {
    return this.http.post<AssignedPartnershipInfo>(`${this.config.dataApiUrl}/subtrips/${subtripId}/partnership/contact`, {contactId: contactId}).pipe(
      switchMap(partnerInfo => {
        return this.replacePartnerAssignmentDates(partnerInfo)
      }),
      tap(result => {
        this.partnershipsUpdated.next({subtripId: subtripId})
      })
    )
  }

  removePartnership(subtripId: string, partnerId: string): Observable<void> {
    return this.http.delete<void>(`${this.config.dataApiUrl}/subtrips/${subtripId}/partnership/partner/${partnerId}`).pipe(
      tap(result => {
        this.partnershipsUpdated.next({subtripId: subtripId})
      })
    )
  }

  removeCommunity(subtripId: string, communityId: string): Observable<void> {
    return this.http.delete<void>(`${this.config.dataApiUrl}/subtrips/${subtripId}/partnership/community/${communityId}`).pipe(
      tap(result => {
        this.partnershipsUpdated.next({subtripId: subtripId})
      })
    )
  }

  removeContact(subtripId: string, contactId: string): Observable<void> {
    return this.http.delete<void>(`${this.config.dataApiUrl}/subtrips/${subtripId}/partnership/contact/${contactId}`).pipe(
      tap(result => {
        this.partnershipsUpdated.next({subtripId: subtripId})
      })
    )
  }

  updatePartnerStatus(subtripId: string, partnerId: string, status: Partial<PartnershipStatus>): Observable<void> {
    return this.http.patch<void>(`${this.config.dataApiUrl}/subtrips/${subtripId}/partnership/partner/${partnerId}/status`, status).pipe(
      tap(result => {
        this.partnershipsUpdated.next({subtripId: subtripId})
      })
    )
  }

  updateCommunityStatus(subtripId: string, communityId: string, status: Partial<PartnershipStatus>): Observable<void> {
    return this.http.patch<void>(`${this.config.dataApiUrl}/subtrips/${subtripId}/partnership/community/${communityId}/status`, status).pipe(
      tap(result => {
        this.partnershipsUpdated.next({subtripId: subtripId})
      })
    )
  }

  updateContactStatus(subtripId: string, contactId: string, status: Partial<PartnershipStatus>): Observable<void> {
    return this.http.patch<void>(`${this.config.dataApiUrl}/subtrips/${subtripId}/partnership/contact/${contactId}/status`, status).pipe(
      tap(result => {
        this.partnershipsUpdated.next({subtripId: subtripId})
      })
    )
  }

  /**
   * Save all partnership changes for a trip
   * This follows the ColdFusion logic: delete all existing partnerships and insert new ones
   */
  savePartnerships(
    tripId: number,
    partnerships: {
      communities: Array<{ id: string; confirmed: number; public: number }>;
      partners: Array<{ id: string; confirmed: number; public: number }>;
      contacts: Array<{ id: string; confirmed: number; public: number }>;
    }
  ): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(
      `${this.config.dataApiUrl}/trips/${tripId}/partnership/save`,
      {
        partnerships: partnerships
      }
    ).pipe(
      tap(result => {
        if (result.success) {
          // Since we don't have subtripId, we'll use a placeholder for the event
          this.partnershipsUpdated.next({subtripId: 'updated'})
        }
      })
    )
  }

  /**
   * Returns a form used for updating Trip Partnership ministries.  For each MinistryType, generates a form with two controls per
   * ministry type.  
   * - "<ministryType>-checked" for whether or not the ministry is selected for this trip
   * - "<ministryType>-description" for the text description of the ministry
   * @param ministries The ministries to use for default values
   * @returns the reactive form
   */
  getMinistryForm(ministries: TripMinistry[], disabled: boolean = false) {
    let group: any = {}

    this.ministryTypes.forEach(ministry => {
      const foundMinistry = ministries.find(m => m.type === ministry)
      group[ministry + '-checked'] = new FormControl({ value: foundMinistry?.checked ?? false, disabled: disabled })
      group[ministry + '-description'] = new FormControl({ value: foundMinistry?.description ?? '', disabled: disabled })
    })

    return new FormGroup(group)
  }

  convertFormValues(ministryFormValues: any) {
    let tripMinistries: TripMinistry[] = []
    for (const type of this.ministryTypes) {
      tripMinistries = [...tripMinistries, { type: type, checked: ministryFormValues[type + '-checked'], description: ministryFormValues[type + '-description'] }]
    }

    return tripMinistries
  }

  replacePartnerAssignmentDatesDirect(partnerInfo: AssignedPartnershipInfo) {
    return {
      subtripId: partnerInfo.subtripId,
      partial: partnerInfo.partial,
      partnership: {
        partners: DataTransform.replaceDatesArrayDirect<ProfilesPartner>(partnerInfo.partnership.partners, this.partnerDateFields) ,
        communities:DataTransform.replaceDatesArrayDirect<ProfilesCommunity>(partnerInfo.partnership.communities, this.communityDateFields), 
        contacts: DataTransform.replaceDatesArrayDirect<ProfilesContact>(partnerInfo.partnership.contacts, this.contactDateFields), 
      },
      assignments: {
        partners: DataTransform.replaceDatesArrayDirect<TripsPartner>(partnerInfo.assignments.partners, this.tripsPartnerDateFields),
        communities:  DataTransform.replaceDatesArrayDirect<TripsCommunity>(partnerInfo.assignments.communities, this.tripsCommunityDateFields), 
        contacts: DataTransform.replaceDatesArrayDirect<TripsContact>(partnerInfo.assignments.contacts, this.tripsContactDateFields) 
      }
    }
  }

  replacePartnerAssignmentDates(partnerInfo: AssignedPartnershipInfo) {
     return forkJoin([
      DataTransform.replaceDatesArray<ProfilesPartner>(partnerInfo.partnership.partners, this.partnerDateFields),
      DataTransform.replaceDatesArray<ProfilesCommunity>(partnerInfo.partnership.communities, this.communityDateFields),
      DataTransform.replaceDatesArray<ProfilesContact>(partnerInfo.partnership.contacts, this.contactDateFields),
      DataTransform.replaceDatesArray<TripsPartner>(partnerInfo.assignments.partners, this.tripsPartnerDateFields),
      DataTransform.replaceDatesArray<TripsCommunity>(partnerInfo.assignments.communities, this.tripsCommunityDateFields),
      DataTransform.replaceDatesArray<TripsContact>(partnerInfo.assignments.contacts, this.tripsContactDateFields)
    ]).pipe(
      switchMap(modifiedProperties => {
        return of({
          subtripId: partnerInfo.subtripId,
          partial: partnerInfo.partial,
          partnership: {
            partners: modifiedProperties[0],
            communities: modifiedProperties[1],
            contacts: modifiedProperties[2]
          },
          assignments: {
            partners: modifiedProperties[3],
            communities: modifiedProperties[4],
            contacts: modifiedProperties[5]
          }
        })
      })
    )
  }

  mergePartnershipInfo(currentInfo: AssignedPartnershipInfo, mergeInfo: AssignedPartnershipInfo): AssignedPartnershipInfo {
    if (currentInfo.subtripId !== mergeInfo.subtripId) throw new Error("Subtrip ID's must match to merge partnership info")
    
    return {
      subtripId: currentInfo.subtripId,
      partial: currentInfo.partial && mergeInfo.partial,
      partnership: {
        partners:  [...currentInfo.partnership.partners, ...mergeInfo.partnership.partners],
        communities:  [...currentInfo.partnership.communities, ...mergeInfo.partnership.communities],
        contacts: [...currentInfo.partnership.contacts, ...mergeInfo.partnership.contacts]
      },
      assignments: {
        partners:  [...currentInfo.assignments.partners, ...mergeInfo.assignments.partners],
        communities:  [...currentInfo.assignments.communities, ...mergeInfo.assignments.communities],
        contacts:  [...currentInfo.assignments.contacts, ...mergeInfo.assignments.contacts]
      }
    }
  }

  removeFromPartnershipInfo(id: string, info: AssignedPartnershipInfo): AssignedPartnershipInfo {
    const returnValue: AssignedPartnershipInfo = {
      subtripId: info.subtripId,
      partial: info.partial,
      partnership: {
        partners: [],
        communities: [],
        contacts: []
      },
      assignments: {
        partners: [],
        communities: [],
        contacts: []
      }
    }

    let index = -1;

    index = info.partnership.partners.findIndex(item => item.id === id)
    if (index >= 0 ) {
      returnValue.partnership.partners = [...info.partnership.partners.slice(0, index), ...info.partnership.partners.slice(index + 1)]
    } else {
      returnValue.partnership.partners = [...info.partnership.partners]
    }

    index = info.partnership.communities.findIndex(item => item.id === id)
    if (index >= 0 ) {
      returnValue.partnership.communities = [...info.partnership.communities.slice(0, index), ...info.partnership.communities.slice(index + 1)]
    } else {
      returnValue.partnership.communities = [...info.partnership.communities]
    }

    index = info.partnership.contacts.findIndex(item => item.id === id)
    if (index >= 0 ) {
      returnValue.partnership.contacts = [...info.partnership.contacts.slice(0, index), ...info.partnership.contacts.slice(index + 1)]
    } else {
      returnValue.partnership.contacts = [...info.partnership.contacts]
    }

    index = info.assignments.partners.findIndex(item => item.partnerId === id)
    if (index >= 0 ) {
      returnValue.assignments.partners = [...info.assignments.partners.slice(0, index), ...info.assignments.partners.slice(index + 1)]
    } else {
      returnValue.assignments.partners = [...info.assignments.partners]
    }

    index = info.assignments.communities.findIndex(item => item.communityId === id)
    if (index >= 0 ) {
      returnValue.assignments.communities = [...info.assignments.communities.slice(0, index), ...info.assignments.communities.slice(index + 1)]
    } else {
      returnValue.assignments.communities = [...info.assignments.communities]
    }

    index = info.assignments.contacts.findIndex(item => item.contactId === id)
    if (index >= 0 ) {
      returnValue.assignments.contacts = [...info.assignments.contacts.slice(0, index), ...info.assignments.contacts.slice(index + 1)]
    } else {
      returnValue.assignments.contacts = [...info.assignments.contacts]
    }

    return returnValue
  }

  emptyPartnershipInfo(): AssignedPartnershipInfo {
    return {
      subtripId: "",
      partial: false,
      partnership: {
        communities: [],
        contacts: [],
        partners: []
      },
      assignments: {
        communities: [],
        contacts: [],
        partners: []
      }
    }
  }

  emptyPartner(): ProfilesPartner {
    return {
      id: "",
      active: 0,
      address1: "",
      address2: "",
      attendavg: 0,
      attendmax: 0,
      capacity: 0,
      city: "",
      classes: 0,
      comId: "",
      cost: 0,
      denomination: "",
      description: "",
      descriptionPublic: "",
      disaster: 0,
      district: "",
      email: "",
      facebook: "",
      fax: "",
      googleplus: "",
      hotelcap: 0,
      hotelrooms: 0,
      inactive: 0,
      lat: 0,
      lng: 0,
      locId: "",
      lodging: 0,
      lodgingNotes: "",
      lodgingType: "",
      mercytype: "",
      minAdult: 0,
      minAdultNotes: "",
      minChild: 0,
      minChildNotes: "",
      minChurch: 0,
      minChurchNotes: "",
      minEvang: 0,
      minEvangNotes: "",
      minMedical: 0,
      minMedicalNotes: "",
      minMercy: 0,
      minMercyNotes: "",
      minOther1: 0,
      minOther1Name: "",
      minOther1Notes: "",
      minOther2: 0,
      minOther2Name: "",
      minOther2Notes: "",
      minPrayer: 0,
      minPrayerNotes: "",
      minSchool: 0,
      minSchoolNotes: "",
      minServe: 0,
      minServeNotes: "",
      minSports: 0,
      minSportsNotes: "",
      minYouth: 0,
      minYouthNotes: "",
      mission: "",
      missionPublic: "",
      modified: new Date(1000, 0, 1),
      name: "",
      oldcomId: 0,
      oldId: 0,
      oldlocId: 0,
      othersocial: "",
      parkCost: 0,
      parkLat: 0,
      parkLng: 0,
      parkNote: "",
      phone1: "",
      phone2: "",
      photo: 0,
      ppm365: 0,
      rating: 0,
      recDefault: 0,
      rectype: "",
      residents: 0,
      resttype: "",
      rooms: 0,
      showers: 0,
      ssid: "",
      state: "",
      students: 0,
      timezone: "",
      toilets: 0,
      tuition: 0,
      twitter: "",
      type: PartnerTypes.None,
      units: 0,
      value: 0,
      video: "",
      vision: "",
      visionPublic: "",
      website: "",
      wifi: 0,
      wifipass: "",
      youtube: "",
      zipcode: ""
    }
  }

  emptyCommunity(): ProfilesCommunity {
    return {
      id: "",
      locId: "",
      active: 0,
      airport: "",
      barriers: "",
      barriersPublic: "",
      distance: 0,
      district: "",
      lat: 0,
      lng: 0,
      modified: new Date(1000, 0, 1),
      name: "",
      needs: "",
      needsPublic: "",
      occ1: "",
      occ2: "",
      occ3: "",
      oldId: 0,
      oldlocId: 0,
      population: "",
      size: 0,
      summary: "",
      summaryPublic: ""
    }
  }

  emptyContact(): ProfilesContact {
    return {
      comId: "",
      id: "",
      locId: "",
      partnerId: "",
      active: 0,
      address1: "",
      address2: "",
      birthday: new Date(1000, 0, 1),
      city: "",
      country: "",
      description: "",
      descriptionPublic: "",
      district: "",
      email: "",
      facebook: "",
      fax: "",
      firstname: "",
      gender: 0,
      googleplus: "",
      jobtitle: "",
      lastname: "",
      lat: 0,
      lng: 0,
      middlename: "",
      modified: new Date(1000, 0, 1)
    }
  }
}
